import { Fragment, useState, type ReactNode, type Key } from 'react';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';

/**
 * DataTable — opaque-white table with sort/pagination.
 *
 * The table body is intentionally NOT glass — readability beats aesthetics
 * for data. The containing `.data-surface` utility renders a clean white
 * card with hairline borders.
 *
 * Generic over the row type T. Each column declares:
 *   - key (unique within this table)
 *   - header (displayed)
 *   - cell (render function returning the cell content for a row)
 *   - sortable (boolean; if true, the header becomes clickable)
 *   - align (left | right | center)
 *   - width (CSS value, optional)
 */

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  width?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => Key;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  emptyMessage?: ReactNode;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  /**
   * Optional collapsible grouping. Rows on the current page are bucketed by
   * `key` in first-seen order and rendered under a collapsible header row.
   * Grouping is per page — the footer pager still spans the whole result set,
   * so a group shows only the members that landed on this page.
   */
  groupBy?: (row: T) => { key: string; label: ReactNode };
}

/** Buckets rows in first-seen order so grouping never reorders the server's sort. */
function buildGroups<T>(rows: T[], groupBy: (row: T) => { key: string; label: ReactNode }) {
  type Group = { key: string; label: ReactNode; rows: T[] };
  const groups: Group[] = [];
  const seen = new Map<string, Group>();
  for (const row of rows) {
    const { key, label } = groupBy(row);
    let group = seen.get(key);
    if (!group) {
      group = { key, label, rows: [] };
      seen.set(key, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  sortBy,
  sortDir,
  onSort,
  page,
  pageSize,
  total,
  onPageChange,
  emptyMessage = 'Nothing to show here yet.',
  loading,
  onRowClick,
  groupBy,
}: DataTableProps<T>) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Collapsed rather than expanded set, so groups default to open and a group
  // that first appears on a later page is open too.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const groups = groupBy ? buildGroups(rows, groupBy) : null;

  const renderRow = (row: T) => (
    <tr
      key={rowKey(row)}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
      className={cn(
        'border-b border-outline-variant/60 last:border-b-0',
        onRowClick && 'cursor-pointer hover:bg-surface-variant/50',
      )}
    >
      {columns.map((col) => (
        <td
          key={col.key}
          style={{ textAlign: col.align ?? 'left' }}
          className="px-3 py-2 text-caption text-on-surface"
        >
          {col.cell(row)}
        </td>
      ))}
    </tr>
  );

  return (
    <div className="data-surface">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm min-w-[40rem]">
          <thead className="bg-surface-container-low">
            <tr>
              {columns.map((col) => {
                const isSorted = sortBy === col.key;
                const Chev = sortDir === 'asc' ? ChevronUp : ChevronDown;
                return (
                  <th
                    key={col.key}
                    style={{ width: col.width, textAlign: col.align ?? 'left' }}
                    aria-sort={
                      isSorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={cn(
                      'uppercase text-micro font-semibold tracking-wide border-b border-outline-variant px-3 py-2',
                      isSorted ? 'text-brand-blue' : 'text-text-secondary',
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => onSort?.(col.key)}
                        className="inline-flex items-center gap-1 select-none uppercase font-semibold tracking-wide rounded-sm"
                      >
                        {col.header}
                        <span className={cn('opacity-40', isSorted && 'opacity-100')}>
                          <Chev className="w-3 h-3" />
                        </span>
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1">{col.header}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-10 text-center text-text-tertiary text-label"
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-10 text-center text-text-tertiary text-label"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
            {groups
              ? groups.map((group) => {
                  const open = !collapsed.has(group.key);
                  return (
                    <Fragment key={group.key}>
                      <tr className="border-b border-outline-variant/60">
                        <td colSpan={columns.length} className="p-0 bg-surface-container-low">
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.key)}
                            aria-expanded={open}
                            className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-surface-variant/50"
                          >
                            <ChevronRight
                              className={cn(
                                'w-4 h-4 shrink-0 text-text-tertiary transition-transform',
                                open && 'rotate-90',
                              )}
                            />
                            <span className="font-semibold text-label text-text-primary truncate">
                              {group.label}
                            </span>
                            <span className="text-caption text-text-tertiary tabular-nums">
                              {group.rows.length}
                            </span>
                          </button>
                        </td>
                      </tr>
                      {open && group.rows.map(renderRow)}
                    </Fragment>
                  );
                })
              : rows.map(renderRow)}
          </tbody>
        </table>
      </div>

      {/* Footer with pagination */}
      <div className="flex items-center justify-between px-3 py-2.5 border-t border-outline-variant bg-surface-container-low">
        <span className="text-caption text-text-tertiary">
          {total === 0
            ? 'No rows'
            : `Showing ${start}–${end} of ${total.toLocaleString('en-IN')}`}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            Prev
          </Button>
          <span className="px-2 text-caption text-text-tertiary tabular-nums">
            {page} / {totalPages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
